import { useState, useRef } from "react";
import "./BusinessPartnerForm.css";

const STEPS = [
  { label: "Contact", icon: "🏢" },
  { label: "Business", icon: "📊" },
  { label: "Partnership", icon: "🤝" },
];

const OPTS = {
  orgType: ["Consulting Firm","Technology Solutions Company","Startup / Innovation Firm","Sales & Distribution Partner"],
  focusAreas: ["AI & Digital Transformation","Enterprise Technology Solutions","Cloud & Infrastructure Services","Business Development & Sales"],
  scale: ["Local / Regional Operations","Multi-State Presence","PAN India Operations","Global Business Operations"],
  partnershipType: ["Strategic Business Alliance","Sales & Revenue Partnership","Technology Collaboration","Referral Partnership"],
  b2bExp: ["Extensive Enterprise Portfolio","Strong Mid-Market Experience","Growing B2B Presence","Startup & SME Focused"],
  collabModel: ["Long-Term Strategic Partnership","Project-Based Collaboration","Joint Go-To-Market Initiatives","Flexible Based on Opportunity"],
  expectedOutcome: ["Revenue Growth Opportunities","Market Expansion","Technology Enablement","Strategic Industry Connections"],
};

export default function BusinessPartnerForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    fullName:"", contactNumber:"", email:"", website:"", bio:null,
    orgType:"", focusAreas:[], scale:"",
    partnershipType:[], b2bExp:"", collabModel:"", expectedOutcome:[],
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleMulti = (k,v) => set(k, form[k].includes(v) ? form[k].filter(x=>x!==v) : [...form[k],v]);

  const validate = () => {
    const e = {};
    if(step===0){
      if(!form.fullName.trim()) e.fullName="Required";
      if(!form.contactNumber.trim()) e.contactNumber="Required";
      if(!form.email.trim()||!/\S+@\S+\.\S+/.test(form.email)) e.email="Valid email required";
      if(!form.website.trim()) e.website="Required";
    }
    if(step===1){
      if(!form.orgType) e.orgType="Required";
      if(!form.focusAreas.length) e.focusAreas="Select at least one";
      if(!form.scale) e.scale="Required";
    }
    if(step===2){
      if(!form.partnershipType.length) e.partnershipType="Select at least one";
      if(!form.b2bExp) e.b2bExp="Required";
      if(!form.collabModel) e.collabModel="Required";
      if(!form.expectedOutcome.length) e.expectedOutcome="Select at least one";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const next = () => { if(validate()) setStep(s=>s+1); };
  const back = () => setStep(s=>s-1);
  
  const submitToBackend = async (formData) => {
    setIsSubmitting(true);
    const submitForm = new FormData();
    
    // Append all form fields
    submitForm.append('fullName', formData.fullName);
    submitForm.append('contactNumber', formData.contactNumber);
    submitForm.append('email', formData.email);
    submitForm.append('website', formData.website);
    submitForm.append('orgType', formData.orgType);
    submitForm.append('focusAreas', JSON.stringify(formData.focusAreas));
    submitForm.append('scale', formData.scale);
    submitForm.append('partnershipType', JSON.stringify(formData.partnershipType));
    submitForm.append('b2bExp', formData.b2bExp);
    submitForm.append('collabModel', formData.collabModel);
    submitForm.append('expectedOutcome', JSON.stringify(formData.expectedOutcome));
    
    // Append file if exists
    if (formData.bio) {
      submitForm.append('bio', formData.bio);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/business-partner/submit`, {
        method: 'POST',
        body: submitForm
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
      } else {
        // Handle validation errors
        const validationErrors = {};
        if (result.errors) {
          result.errors.forEach(err => {
            validationErrors[err.param] = err.msg;
          });
        }
        setErrors(validationErrors);
        alert(result.message || 'Please fix the errors in the form');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit application. Please make sure the backend server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (e) => { 
    e.preventDefault(); 
    if (validate()) {
      submitToBackend(form);
    }
  };

  if(submitted) return (
    <div className="bpf bpf--success">
      <div className="bpf__success-badge">✦</div>
      <h2>Partnership Inquiry Submitted</h2>
      <p>Thank you, <strong>{form.fullName}</strong>. We've received your business partner application. Our partnerships team will connect with you within 3–5 business days.</p>
      <button className="bpf__btn bpf__btn--primary" onClick={()=>{setSubmitted(false);setStep(0);setForm({
        fullName:"", contactNumber:"", email:"", website:"", bio:null,
        orgType:"", focusAreas:[], scale:"",
        partnershipType:[], b2bExp:"", collabModel:"", expectedOutcome:[],
      });}}>Submit Another</button>
    </div>
  );

  return (
    <div className="bpf">
      <div className="bpf__header">
        <span className="bpf__eyebrow">Strategic Alliances</span>
        <h1 className="bpf__title">Business Partner <span>Application</span></h1>
        <p className="bpf__subtitle">Build a strategic alliance with Bairees and grow together in the AI transformation space.</p>
      </div>

      <div className="bpf__stepper">
        {STEPS.map((s,i)=>(
          <div key={i} className={`bpf__step ${i===step?"bpf__step--active":""} ${i<step?"bpf__step--done":""}`}>
            <div className="bpf__step-circle">{i<step?"✓":i+1}</div>
            <div className="bpf__step-info">
              <div className="bpf__step-label">{s.icon} {s.label}</div>
              {i<2 && <div className={`bpf__step-connector ${i<step?"bpf__step-connector--done":""}`} />}
            </div>
          </div>
        ))}
      </div>

      <form className="bpf__form" onSubmit={submit} noValidate>
        {step===0 && (
          <div className="bpf__panel">
            <h3 className="bpf__panel-title">Contact Information</h3>
            <div className="bpf__grid bpf__grid--2">
              <F label="Full Name *" error={errors.fullName}>
                <input className={`bpf__input ${errors.fullName?"bpf__input--err":""}`} value={form.fullName} onChange={e=>set("fullName",e.target.value)} placeholder="Rajesh Kumar" />
              </F>
              <F label="Contact Number *" error={errors.contactNumber}>
                <input className={`bpf__input ${errors.contactNumber?"bpf__input--err":""}`} value={form.contactNumber} onChange={e=>set("contactNumber",e.target.value)} placeholder="+91 98765 43210" />
              </F>
            </div>
            <F label="Professional Email *" error={errors.email}>
              <input className={`bpf__input ${errors.email?"bpf__input--err":""}`} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="rajesh@yourcompany.com" />
            </F>
            <F label="Website / LinkedIn *" error={errors.website}>
              <input className={`bpf__input ${errors.website?"bpf__input--err":""}`} value={form.website} onChange={e=>set("website",e.target.value)} placeholder="https://yourcompany.com" />
            </F>
            <F label="Upload Company Bio / Deck">
              <div className="bpf__file" onClick={()=>fileRef.current.click()}>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" hidden onChange={e=>set("bio",e.target.files[0])} />
                <div className="bpf__file-icon">🏢</div>
                <div>
                  <div className="bpf__file-main">{form.bio?form.bio.name:"Upload company profile or pitch deck"}</div>
                  <div className="bpf__file-hint">PDF, Word, or PPT supported</div>
                </div>
              </div>
            </F>
          </div>
        )}

        {step===1 && (
          <div className="bpf__panel">
            <h3 className="bpf__panel-title">Business Overview</h3>
            <F label="Organization Type *" error={errors.orgType}>
              <div className="bpf__org-grid">
                {OPTS.orgType.map(o=>(
                  <label key={o} className={`bpf__org-card ${form.orgType===o?"bpf__org-card--active":""}`}>
                    <input type="radio" name="orgType" value={o} checked={form.orgType===o} onChange={()=>set("orgType",o)} />
                    <span className="bpf__org-dot" />
                    {o}
                  </label>
                ))}
              </div>
            </F>
            <F label="Primary Business Focus Areas * (select all that apply)" error={errors.focusAreas}>
              <div className="bpf__chip-group">
                {OPTS.focusAreas.map(o=>(
                  <button key={o} type="button" className={`bpf__chip ${form.focusAreas.includes(o)?"bpf__chip--active":""}`} onClick={()=>toggleMulti("focusAreas",o)}>{o}</button>
                ))}
              </div>
            </F>
            <F label="Scale of Business Operations *" error={errors.scale}>
              <div className="bpf__scale-group">
                {OPTS.scale.map((o,i)=>(
                  <label key={o} className={`bpf__scale-item ${form.scale===o?"bpf__scale-item--active":""}`}>
                    <input type="radio" name="scale" value={o} checked={form.scale===o} onChange={()=>set("scale",o)} />
                    <div className="bpf__scale-dots">
                      {Array(i+1).fill(0).map((_,j)=><div key={j} className="bpf__scale-dot" />)}
                    </div>
                    {o}
                  </label>
                ))}
              </div>
            </F>
          </div>
        )}

        {step===2 && (
          <div className="bpf__panel">
            <h3 className="bpf__panel-title">Partnership Preferences</h3>
            <F label="Type of Partnership Interest * (select all that apply)" error={errors.partnershipType}>
              <div className="bpf__chip-group">
                {OPTS.partnershipType.map(o=>(
                  <button key={o} type="button" className={`bpf__chip ${form.partnershipType.includes(o)?"bpf__chip--active":""}`} onClick={()=>toggleMulti("partnershipType",o)}>{o}</button>
                ))}
              </div>
            </F>
            <div className="bpf__grid bpf__grid--2">
              <F label="Enterprise & B2B Experience *" error={errors.b2bExp}>
                <div className="bpf__radio-group">
                  {OPTS.b2bExp.map(o=>(
                    <label key={o} className={`bpf__radio ${form.b2bExp===o?"bpf__radio--active":""}`}>
                      <input type="radio" name="b2bExp" value={o} checked={form.b2bExp===o} onChange={()=>set("b2bExp",o)} />
                      {o}
                    </label>
                  ))}
                </div>
              </F>
              <F label="Preferred Collaboration Model *" error={errors.collabModel}>
                <div className="bpf__radio-group">
                  {OPTS.collabModel.map(o=>(
                    <label key={o} className={`bpf__radio ${form.collabModel===o?"bpf__radio--active":""}`}>
                      <input type="radio" name="collabModel" value={o} checked={form.collabModel===o} onChange={()=>set("collabModel",o)} />
                      {o}
                    </label>
                  ))}
                </div>
              </F>
            </div>
            <F label="Expected Business Outcomes * (select all that apply)" error={errors.expectedOutcome}>
              <div className="bpf__outcome-group">
                {OPTS.expectedOutcome.map(o=>(
                  <label key={o} className={`bpf__outcome-card ${form.expectedOutcome.includes(o)?"bpf__outcome-card--active":""}`}>
                    <input type="checkbox" checked={form.expectedOutcome.includes(o)} onChange={()=>toggleMulti("expectedOutcome",o)} />
                    <span className="bpf__outcome-check">{form.expectedOutcome.includes(o)?"✓":""}</span>
                    {o}
                  </label>
                ))}
              </div>
            </F>
          </div>
        )}

        <div className="bpf__actions">
          {step>0 && <p  className="bpf__btn bpf__btn--ghost" onClick={back}>← Back</p>}
          {step<2
            ? <p  className="bpf__btn bpf__btn--primary" onClick={next}>Continue →</p>
            : <button type="submit" className="bpf__btn bpf__btn--primary" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Application ✦"}
              </button>
          }
        </div>
      </form>
    </div>
  );
}

function F({label,error,children}){
  return (
    <div className="bpf__field">
      <label className="bpf__label">{label}</label>
      {children}
      {error && <span className="bpf__error">{error}</span>}
    </div>
  );
}